import { useEffect, useState, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import { SocketEvent } from '@swtorbit/shared-socket-events';
// No-op function for production builds
const noop = () => { };
// Simple logger that only logs in development
// Uses IIFE to ensure dead code elimination in production builds
const log = {
    debug: process.env.NODE_ENV === 'development'
        ? (...args) => { /* eslint-disable-next-line no-console */ console.log('[Socket]', ...args); }
        : noop,
    error: process.env.NODE_ENV === 'development'
        ? (...args) => { /* eslint-disable-next-line no-console */ console.error('[Socket]', ...args); }
        : noop,
    warn: process.env.NODE_ENV === 'development'
        ? (...args) => { /* eslint-disable-next-line no-console */ console.warn('[Socket]', ...args); }
        : noop,
};
export function useSocket(url, options) {
    const [socket, setSocket] = useState(null);
    const [isConnected, setIsConnected] = useState(false);
    const [connectionError, setConnectionError] = useState(null);
    // Track if we've ever been connected (to detect reconnection vs initial connect)
    const hasConnectedOnceRef = useRef(false);
    // Keep refs updated for callbacks
    const onTokensReceivedRef = useRef(options?.onTokensReceived);
    const onReconnectRef = useRef(options?.onReconnect);
    useEffect(() => {
        onTokensReceivedRef.current = options?.onTokensReceived;
    }, [options?.onTokensReceived]);
    useEffect(() => {
        onReconnectRef.current = options?.onReconnect;
    }, [options?.onReconnect]);
    useEffect(() => {
        // Reset connection tracking when URL changes
        hasConnectedOnceRef.current = false;
        const newSocket = io(url, {
            autoConnect: true,
            reconnection: true,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
        });
        newSocket.on('connect', () => {
            log.debug('Connected to server');
            // Check if this is a reconnection (not initial connect)
            if (hasConnectedOnceRef.current) {
                log.warn('Socket reconnected - triggering re-authentication');
                if (onReconnectRef.current) {
                    onReconnectRef.current();
                }
            }
            else {
                // Mark that we've connected at least once
                hasConnectedOnceRef.current = true;
            }
            setIsConnected(true);
            setConnectionError(null);
        });
        newSocket.on('disconnect', (reason) => {
            log.debug('Disconnected from server:', reason);
            setIsConnected(false);
        });
        newSocket.on('error', (error) => {
            log.error('Socket error:', error);
        });
        newSocket.on('connect_error', (error) => {
            log.error('Connection error:', error.message);
            setConnectionError(error.message);
            setIsConnected(false);
        });
        // Listen for auth tokens
        newSocket.on(SocketEvent.AUTH_TOKENS, (tokens) => {
            log.debug('Received auth tokens');
            if (onTokensReceivedRef.current) {
                onTokensReceivedRef.current(tokens);
            }
        });
        setSocket(newSocket);
        return () => {
            // Explicitly remove listeners before closing for clarity
            newSocket.off('connect');
            newSocket.off('disconnect');
            newSocket.off('error');
            newSocket.off('connect_error');
            newSocket.off(SocketEvent.AUTH_TOKENS);
            newSocket.close();
        };
    }, [url]);
    const authenticate = useCallback((authData) => {
        if (!socket)
            return;
        socket.emit(SocketEvent.AUTH_REQUEST, authData);
    }, [socket]);
    return { socket, isConnected, connectionError, authenticate };
}
